// src/audit.ts
import { z as z2 } from "zod";

// src/helpers.ts
import { z } from "zod";
var ulid = z.string().length(26).regex(/^[0-9A-HJKMNP-TV-Z]{26}$/);
var SUBDOMAIN_MESSAGE = "Subdomain must be 1\u201350 characters and contain only lowercase letters, numbers, and hyphens (no leading, trailing, or consecutive hyphens).";
var subDomain = z.string().min(1, { message: SUBDOMAIN_MESSAGE }).max(50, { message: SUBDOMAIN_MESSAGE }).regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, { message: SUBDOMAIN_MESSAGE });
var awsAccount = z.string().min(12).max(12).regex(/^\d{12}$/);
var region = z.string().min(2).max(20).regex(/^[a-z]{2}-[a-z]+-\d{1}$/, {
  message: "Invalid AWS region format"
});
var isoDate = z.string().refine(
  (val) => {
    const isoRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?)?$/;
    if (!isoRegex.test(val)) {
      return false;
    }
    const date = new Date(val);
    return !isNaN(date.getTime());
  },
  {
    message: "Invalid ISO timestamp"
  }
);
var nonEmptyString = z.string().min(1).refine((val) => val.trim().length > 0, {
  message: "String cannot be empty or whitespace only"
});
var nonEmptyLowercaseString = z.string().min(1).toLowerCase().refine((val) => val.trim().length > 0, {
  message: "String cannot be empty or whitespace only"
});
var paginationOptionsSchema = z.object({
  limit: z.number().int().min(1).max(100).optional().default(20),
  nextToken: z.string().optional()
});
var createPaginatedResponseSchema = (itemSchema) => z.object({
  items: z.array(itemSchema),
  totalCount: z.number().int().nonnegative().optional(),
  nextToken: z.string().optional(),
  hasMore: z.boolean()
});

// src/audit.ts
var AuditActionEnum = z2.enum([
  "PolicyCreated",
  "PolicyUpdated",
  "PolicyDeleted",
  "PolicyAssigned",
  "ReminderSent",
  "BulkReminderSent",
  "AssignmentRevoked",
  "BulkAssignmentRevoked",
  // Assignment due-date edit (#2376) — emitted from the policy-service
  // sub-account API when an admin edits the dueDate on a pending
  // assignment via `PATCH /api/assignments/:id`. Metadata carries
  // `previousDueDate`, `newDueDate`, and the snapshotted policy `title`.
  "AssignmentDueDateChanged",
  "UserCreated",
  "UserUpdated",
  "UserDeactivated",
  "UserReactivated",
  "UserDeleted",
  "UserPasswordResetSent",
  "UserTestModeChanged",
  "GroupCreated",
  "GroupRenamed",
  "GroupDeleted",
  "GroupArchived",
  "GroupUnarchived",
  "GroupMembersAdded",
  "GroupMembersRemoved",
  // Billing actions (#1878) — emitted from the Stripe webhook handler when a
  // subscription event changes a tenant's tier or user-pack quantity. Stripe
  // retains its own audit log; these entries record only the side effect we
  // applied on our side (tier flip, pack-quantity change, billing-customer
  // metadata update).
  "TierUpgraded",
  "TierDowngraded",
  "UserPackQuantityChanged",
  "CustomerUpdated",
  // Failed-payment / dunning actions (#1880) — emitted from the Stripe webhook
  // handler when a payment fails (`invoice.payment_failed`) or when Stripe
  // cancels a subscription after retries are exhausted
  // (`customer.subscription.deleted` with cancellation_details.reason ===
  // 'payment_failed'). Both record only our local side effect; Stripe retains
  // its own dunning audit log.
  "PaymentFailed",
  "SubscriptionSuspended",
  // Over-provisioned-packs notifier action (#1881) — emitted by the monthly
  // scheduled Lambda when a Standard-tier tenant's purchased user-pack count
  // is more than enough to cover its actual active user count and we send
  // the billing admin a one-per-month "you may be overpaying" email. The
  // audit row is written BEFORE the email send so a transport failure does
  // not also lose the audit record.
  "OverProvisionedPacksNotified",
  // SSO actions (#1115) — emitted from the PreSignUp Cognito trigger after a
  // federated (SAML) identity is linked to a tenant-side user record during
  // JIT provisioning. The matching `UserCreated` audit is emitted by the
  // existing user-service path on the same flow.
  "UserSamlLinked",
  // SAML attribute-sync action (#2589) — emitted from the user-service
  // PreTokenGeneration Cognito trigger when a federated (SAML) sign-in
  // surfaces IdP name claims (`given_name`/`family_name`) that differ from
  // the stored Cognito + app-DB values and we refresh them. The audit
  // metadata carries ONLY the NAMES of the changed claim fields
  // (`changedFields`) — never the claim values, which are PII.
  "UserSamlAttributesSynced",
  // SCIM actions (#2240 — SCIM phase 1) — emitted from the user-service
  // when a tenant admin creates or revokes a SCIM bearer token from the
  // Settings UI. The audit row records the public `tokenId` and the
  // acting admin's `userId`; the plaintext token never leaves the create
  // response and is never written to an audit detail.
  "scim_token_created",
  "scim_token_revoked",
  // SCIM user-provisioning actions (#2241 — SCIM phase 3) — emitted by
  // the SCIM service-provider Lambda when an IdP-side connector creates,
  // replaces, or deactivates a user via /scim/v2/Users. Distinct from the
  // human-driven `UserCreated`/`UserUpdated`/`UserDeactivated` actions so
  // an admin can tell at a glance whether a change was driven by their IdP
  // or by an interactive admin. The matching audit detail carries
  // `source: 'scim'` and the public `tokenId` of the bearer that drove
  // the request, never the plaintext token.
  "scim_user_created",
  "scim_user_updated",
  "scim_user_deactivated",
  // SCIM group-provisioning actions (#2243 — SCIM phase 5) — emitted by
  // the SCIM service-provider Lambda when an IdP-side connector creates,
  // updates, or deletes a group via /scim/v2/Groups. Distinct from the
  // human-driven `GroupCreated`/`GroupRenamed`/`GroupDeleted` actions so
  // an admin can tell at a glance whether a change was driven by their IdP
  // or by an interactive admin. The matching audit detail carries
  // `source: 'scim'` and the public `tokenId` of the bearer that drove
  // the request, never the plaintext token. `scim_group_updated` covers
  // both rename and membership changes — the metadata records exactly
  // what changed (renamed name, added/removed member ULIDs).
  "scim_group_created",
  "scim_group_updated",
  "scim_group_deleted",
  // Support-ticket actions (#2303 part 1) — emitted from the sub-account
  // support-service controllers when a tenant admin creates, replies to,
  // closes, or reopens a support ticket. Part 2 (#2304) will additionally
  // emit these on the TM-side reply path with `actorEmail` set to the
  // Accedo support staffer's email. Audit publish happens BEFORE
  // res.json (serverless-express gotcha — see CLAUDE.md).
  "SupportTicketCreated",
  "SupportTicketReplied",
  "SupportTicketClosed",
  "SupportTicketReopened"
]);
var AuditResourceTypeEnum = z2.enum(["policy", "policy-assignment", "user", "group", "billing", "scim-token", "support-ticket"]);
var BaseAdminActionFields = z2.object({
  tenantId: ulid,
  actorId: z2.string().min(1),
  actorEmail: z2.string().min(1),
  resourceType: AuditResourceTypeEnum,
  resourceId: z2.string().min(1),
  timestamp: isoDate,
  ipAddress: z2.string().min(1),
  /**
   * Origin of the action (#2241 — SCIM phase 3, narrowed in #2384).
   *
   * Optional for backward compatibility with pre-#2241 audit rows that
   * have no source. When emitted from the SCIM service-provider Lambda
   * this is set to `'scim'`; admin-API and Stripe-webhook callers leave
   * it undefined (implicit `'admin'` / `'system'`).
   *
   * Narrowed to a closed enum in #2384 so the Activity Log
   * `?source=scim` filter has a single canonical value to match on and
   * future provenance values must be added deliberately (e.g.
   * `'bulk-upload'` for CSV imports). Existing audit rows whose
   * `source` is undefined remain compatible.
   */
  source: z2.enum(["scim"]).optional(),
  /**
   * Public SCIM bearer-token ID (#2241 — SCIM phase 3).
   *
   * When `source === 'scim'`, this carries the ULID of the bearer token
   * that drove the change so a tenant admin can trace a row back to
   * which IdP integration produced it. NEVER carries the plaintext
   * token — the plaintext leaves the system only once at create-time.
   */
  tokenId: z2.string().min(1).optional()
});
var adminActionArms = [
  // --- policy (resourceType: 'policy') ---
  BaseAdminActionFields.extend({ action: z2.literal("PolicyCreated"), metadata: z2.object({ title: z2.string() }) }),
  BaseAdminActionFields.extend({ action: z2.literal("PolicyUpdated"), metadata: z2.object({ title: z2.string(), newVersion: z2.number().optional() }) }),
  BaseAdminActionFields.extend({ action: z2.literal("PolicyDeleted"), metadata: z2.object({ title: z2.string() }) }),
  // --- policy-assignment (resourceType: 'policy-assignment') ---
  BaseAdminActionFields.extend({
    action: z2.literal("PolicyAssigned"),
    metadata: z2.object({ title: z2.string(), assignedUserIds: z2.array(z2.string()).optional(), count: z2.number() })
  }),
  BaseAdminActionFields.extend({ action: z2.literal("ReminderSent"), metadata: z2.object({ title: z2.string(), targetUserId: z2.string() }) }),
  BaseAdminActionFields.extend({ action: z2.literal("BulkReminderSent"), metadata: z2.object({ title: z2.string() }) }),
  // `policyId` (#2888): the parent policy's id, written so the Activity
  // Log can link the row to `/policies/:policyId` — these rows'
  // `resourceId` is the ASSIGNMENT id, which has no detail page. Optional
  // for legacy rows (they render plain text). Declared here because the
  // metadata objects parse in STRIP mode: an undeclared key would be
  // silently dropped by the write-path `validateEvent` AND the read-path
  // `validateAuditRow` before the UI ever saw it. Deliberately a lenient
  // `z.string().min(1)` rather than `ulid` — a malformed value would make
  // the emit-time parse throw and silently drop the WHOLE audit event
  // (see the warning on BaseAdminActionFields).
  BaseAdminActionFields.extend({
    action: z2.literal("AssignmentRevoked"),
    metadata: z2.object({ title: z2.string(), reason: z2.string().optional(), policyId: z2.string().min(1).optional() })
  }),
  BaseAdminActionFields.extend({
    action: z2.literal("AssignmentDueDateChanged"),
    metadata: z2.object({
      title: z2.string(),
      previousDueDate: z2.string().nullable(),
      newDueDate: z2.string().nullable(),
      policyId: z2.string().min(1).optional()
    })
  }),
  BaseAdminActionFields.extend({
    action: z2.literal("BulkAssignmentRevoked"),
    metadata: z2.object({
      assignmentIds: z2.array(z2.string()),
      reason: z2.string().optional(),
      revoked: z2.number(),
      skipped: z2.number(),
      errors: z2.array(z2.string())
    })
  }),
  // --- user (resourceType: 'user') ---
  // UserCreated metadata is OPTIONAL at the key level: the admin path
  // writes `{ name }`, the SAML-JIT path writes `{ source: 'SAML_JIT' }`.
  BaseAdminActionFields.extend({
    action: z2.literal("UserCreated"),
    metadata: z2.object({ name: z2.string().optional(), source: z2.string().optional() }).optional()
  }),
  BaseAdminActionFields.extend({ action: z2.literal("UserUpdated"), metadata: z2.object({ name: z2.string() }) }),
  BaseAdminActionFields.extend({ action: z2.literal("UserDeactivated"), metadata: z2.object({ name: z2.string() }) }),
  BaseAdminActionFields.extend({ action: z2.literal("UserReactivated"), metadata: z2.object({ name: z2.string() }) }),
  BaseAdminActionFields.extend({ action: z2.literal("UserPasswordResetSent"), metadata: z2.object({ name: z2.string() }) }),
  BaseAdminActionFields.extend({ action: z2.literal("UserDeleted"), metadata: z2.object({ name: z2.string() }) }),
  BaseAdminActionFields.extend({
    action: z2.literal("UserTestModeChanged"),
    metadata: z2.object({ before: z2.boolean(), after: z2.boolean(), name: z2.string() })
  }),
  BaseAdminActionFields.extend({ action: z2.literal("UserSamlLinked"), metadata: z2.object({ samlSubject: z2.string() }) }),
  // SAML attribute-sync (#2589). Metadata carries ONLY the names of the
  // claim fields that changed (e.g. `['family_name']`) — never the values,
  // which are PII. `changedFields` is always present (may be empty only in
  // tests; the producer never emits this action with zero changes).
  BaseAdminActionFields.extend({ action: z2.literal("UserSamlAttributesSynced"), metadata: z2.object({ changedFields: z2.array(z2.string()) }) }),
  // --- group (resourceType: 'group') ---
  BaseAdminActionFields.extend({ action: z2.literal("GroupCreated"), metadata: z2.object({ name: z2.string() }) }),
  BaseAdminActionFields.extend({ action: z2.literal("GroupArchived"), metadata: z2.object({ name: z2.string() }) }),
  BaseAdminActionFields.extend({ action: z2.literal("GroupUnarchived"), metadata: z2.object({ name: z2.string() }) }),
  BaseAdminActionFields.extend({ action: z2.literal("GroupRenamed"), metadata: z2.object({ name: z2.string() }) }),
  BaseAdminActionFields.extend({ action: z2.literal("GroupDeleted"), metadata: z2.object({ name: z2.string() }) }),
  BaseAdminActionFields.extend({ action: z2.literal("GroupMembersAdded"), metadata: z2.object({ name: z2.string(), userIds: z2.array(z2.string()) }) }),
  BaseAdminActionFields.extend({ action: z2.literal("GroupMembersRemoved"), metadata: z2.object({ name: z2.string(), userIds: z2.array(z2.string()) }) }),
  // --- scim bearer-token (resourceType: 'scim-token') ---
  BaseAdminActionFields.extend({ action: z2.literal("scim_token_created"), metadata: z2.object({ name: z2.string() }) }),
  BaseAdminActionFields.extend({ action: z2.literal("scim_token_revoked"), metadata: z2.object({ name: z2.string() }) }),
  // --- scim user provisioning (resourceType: 'user') ---
  BaseAdminActionFields.extend({ action: z2.literal("scim_user_created"), metadata: z2.object({ email: z2.string(), externalId: z2.string().optional() }) }),
  BaseAdminActionFields.extend({
    action: z2.literal("scim_user_updated"),
    metadata: z2.object({ email: z2.string(), changedFields: z2.array(z2.string()).optional() })
  }),
  BaseAdminActionFields.extend({ action: z2.literal("scim_user_deactivated"), metadata: z2.object({ email: z2.string() }) }),
  // --- scim group provisioning (resourceType: 'group') ---
  BaseAdminActionFields.extend({
    action: z2.literal("scim_group_created"),
    metadata: z2.object({ displayName: z2.string(), memberCount: z2.number(), externalId: z2.string().optional() })
  }),
  BaseAdminActionFields.extend({
    action: z2.literal("scim_group_updated"),
    metadata: z2.object({
      displayName: z2.string(),
      changedFields: z2.array(z2.string()),
      addedMembers: z2.array(z2.string()),
      removedMembers: z2.array(z2.string())
    })
  }),
  BaseAdminActionFields.extend({ action: z2.literal("scim_group_deleted"), metadata: z2.object({ displayName: z2.string(), memberCount: z2.number() }) }),
  // --- billing (resourceType: 'billing') ---
  BaseAdminActionFields.extend({
    action: z2.literal("TierUpgraded"),
    metadata: z2.object({
      stripeEventId: z2.string(),
      previousTier: z2.string().nullable(),
      newTier: z2.string(),
      previousUserPackQuantity: z2.number().nullable(),
      newUserPackQuantity: z2.number().nullable()
    })
  }),
  BaseAdminActionFields.extend({
    action: z2.literal("UserPackQuantityChanged"),
    metadata: z2.object({
      stripeEventId: z2.string(),
      previousTier: z2.string().nullable(),
      newTier: z2.string(),
      previousUserPackQuantity: z2.number().nullable(),
      newUserPackQuantity: z2.number().nullable()
    })
  }),
  BaseAdminActionFields.extend({ action: z2.literal("TierDowngraded"), metadata: z2.object({ stripeEventId: z2.string(), previousTier: z2.string() }) }),
  BaseAdminActionFields.extend({
    action: z2.literal("SubscriptionSuspended"),
    metadata: z2.object({ stripeEventId: z2.string(), cancellationReason: z2.string().nullable() })
  }),
  BaseAdminActionFields.extend({ action: z2.literal("PaymentFailed"), metadata: z2.object({ stripeEventId: z2.string(), recipientResolved: z2.boolean() }) }),
  BaseAdminActionFields.extend({
    action: z2.literal("CustomerUpdated"),
    metadata: z2.object({ stripeEventId: z2.string(), email: z2.string().nullable(), name: z2.string().nullable() })
  }),
  BaseAdminActionFields.extend({
    action: z2.literal("OverProvisionedPacksNotified"),
    metadata: z2.object({
      auditId: z2.string(),
      packQuantity: z2.number(),
      activeUserCount: z2.number(),
      packsToSave: z2.number(),
      annualSavings: z2.number()
    })
  }),
  // --- support-ticket (resourceType: 'support-ticket') ---
  BaseAdminActionFields.extend({ action: z2.literal("SupportTicketCreated"), metadata: z2.object({ subject: z2.string() }) }),
  BaseAdminActionFields.extend({
    action: z2.literal("SupportTicketReplied"),
    metadata: z2.object({ messageId: z2.string(), authorSide: z2.literal("tm").optional(), origin: z2.literal("tm-api").optional() })
  }),
  BaseAdminActionFields.extend({
    action: z2.literal("SupportTicketClosed"),
    metadata: z2.object({ closedReason: z2.string(), closedBySide: z2.literal("tm").optional(), origin: z2.literal("tm-api").optional() })
  }),
  BaseAdminActionFields.extend({
    action: z2.literal("SupportTicketReopened"),
    metadata: z2.object({ origin: z2.literal("tm-api").optional() }).optional()
  })
];
var AdminActionEventDetailSchema = z2.discriminatedUnion("action", adminActionArms);
var AuditRecordSchema = z2.discriminatedUnion(
  "action",
  adminActionArms.map((arm) => arm.extend({ auditId: ulid }))
);

// src/auditor-link.ts
import { z as z3 } from "zod";
var LinkIdSchema = z3.string().length(43).regex(/^[A-Za-z0-9_-]{43}$/);
var UlidSchema = z3.string().length(26).regex(/^[0-9A-HJKMNP-TV-Z]{26}$/);
var AuditorLinkStatusSchema = z3.enum(["active", "expired", "revoked"]);
var AuditorLinkRecordSchema = z3.object({
  linkId: LinkIdSchema,
  tenantId: UlidSchema,
  packetS3Key: z3.string().min(1),
  createdAt: z3.string().datetime(),
  expiresAt: z3.string().datetime(),
  createdByUserId: UlidSchema,
  revokedAt: z3.string().datetime().optional(),
  label: z3.string().max(120).optional()
});
var AuditorLinkSummarySchema = z3.object({
  linkId: LinkIdSchema,
  url: z3.string().url(),
  createdAt: z3.string().datetime(),
  expiresAt: z3.string().datetime(),
  revokedAt: z3.string().datetime().nullable(),
  status: AuditorLinkStatusSchema,
  label: z3.string().max(120).optional()
});
var CreateAuditorLinkRequestSchema = z3.object({
  expiresInDays: z3.number().int().min(1).max(90).optional(),
  label: z3.string().trim().max(120).optional()
}).strict();
var CreateAuditorLinkResponseSchema = z3.object({
  linkId: LinkIdSchema,
  url: z3.string().url(),
  expiresAt: z3.string().datetime()
});

// src/billing.ts
import { z as z4 } from "zod";
var CheckoutSessionRequestSchema = z4.strictObject({
  /** ULID of the tenant whose admin clicked "Upgrade Plan". */
  tenantId: z4.string(),
  /** URL Stripe redirects to after a successful checkout. */
  successUrl: z4.string().url(),
  /** URL Stripe redirects to if the admin cancels checkout. */
  cancelUrl: z4.string().url()
});
var CheckoutSessionResponseSchema = z4.strictObject({
  /** Hosted Stripe Checkout URL (`https://checkout.stripe.com/c/pay/…`). */
  url: z4.string().url()
});
var UpdateUserPackQuantityRequestSchema = z4.strictObject({
  /** ULID of the tenant whose admin clicked "Buy more seats". */
  tenantId: z4.string(),
  /** Absolute target pack quantity. 0 deletes the pack item entirely. */
  quantity: z4.number().int().min(0).max(1e3)
});
var UpdateUserPackQuantityResponseSchema = z4.strictObject({
  /** Stripe subscription id (`sub_…`) the pack item lives on. */
  subscriptionId: z4.string(),
  /** Post-update pack quantity (0 when the pack item was deleted). */
  quantity: z4.number().int().min(0)
});
var BillingPortalSessionRequestSchema = z4.strictObject({
  /** ULID of the tenant whose admin clicked "Manage Billing". */
  tenantId: z4.string(),
  /** URL Stripe redirects to after the admin closes the portal. */
  returnUrl: z4.string().url()
});
var BillingPortalSessionResponseSchema = z4.strictObject({
  /** Hosted Stripe Customer Portal URL (`https://billing.stripe.com/session/…`). */
  url: z4.string().url()
});
var PaymentMethodSummarySchema = z4.strictObject({
  /** Card brand as reported by Stripe (`visa`, `mastercard`, …). */
  brand: z4.string(),
  /** Last four digits of the card number. */
  last4: z4.string(),
  /** Card expiration month, 1-12. */
  expMonth: z4.number().int().min(1).max(12),
  /** Card expiration year (4-digit). */
  expYear: z4.number().int()
});
var BillingSummaryRequestSchema = z4.strictObject({
  /** ULID of the tenant whose admin opened the Billing panel. */
  tenantId: z4.string()
});
var BillingSummaryResponseSchema = z4.strictObject({
  /** Whether the tenant has an active Stripe subscription. */
  hasSubscription: z4.boolean(),
  /** Total amount charged per billing period, in minor units (cents). Sum of unit_amount * quantity across all subscription items. */
  amount: z4.number().int().nonnegative().optional(),
  /**
   * Three-letter ISO-4217 currency code, lowercase (e.g. `usd`). Letters are
   * validated, not just length (not case, to tolerate Stripe's lowercase
   * convention) — `formatAmount` in `SubscriptionSummary.tsx` passes this
   * straight to `Intl.NumberFormat`, which throws a RangeError on a
   * non-ISO-4217 code. `.length(3)` alone let a 3-character non-letter
   * value (e.g. `'1$2'`) through the schema and into that RangeError
   * (#3338, N10 from the #3337 review).
   */
  currency: z4.string().regex(/^[A-Za-z]{3}$/).optional(),
  /** Billing interval. */
  interval: z4.enum(["day", "week", "month", "year"]).optional(),
  /**
   * ISO-8601 UTC timestamp of the current billing period's end (next
   * renewal, unless cancelAtPeriodEnd). Validated as a real datetime —
   * `SubscriptionSummary.tsx` passes this straight to `new Date(...)` and
   * an `Intl.DateTimeFormat`, which throws `RangeError: Invalid time
   * value` on an unparseable string.
   */
  currentPeriodEnd: z4.string().datetime().optional(),
  /** True when the subscription is set to cancel at the end of the current period ("Ends on" rather than "Renews on"). */
  cancelAtPeriodEnd: z4.boolean().optional(),
  /** The subscription's default payment method, when Stripe has one on file. */
  paymentMethod: PaymentMethodSummarySchema.optional()
});

// src/billing-provision.ts
import { z as z5 } from "zod";
var BillingProvisionRequestSchema = z5.strictObject({
  /**
   * When `true` (the default), the endpoint creates both a Stripe
   * customer AND a subscription at the tenant's current tier's price.
   * When `false`, only the customer is created.
   */
  createSubscription: z5.boolean().default(true),
  /**
   * Stripe `collection_method` to use on the new subscription.
   * Defaults to `'send_invoice'` so the operator does not need to add a
   * card before provisioning.
   */
  collectionMethod: z5.enum(["send_invoice", "charge_automatically"]).default("send_invoice")
});
var BillingProvisionResponseSchema = z5.object({
  /** Stripe customer id (`cus_…`) — either freshly created or already on the tenant row. */
  stripeCustomerId: z5.string(),
  /** Stripe subscription id (`sub_…`). Omitted when `createSubscription: false`. */
  stripeSubscriptionId: z5.string().optional(),
  /** Always `true` — the endpoint always writes `hasBillingAccount: true` before responding. */
  hasBillingAccount: z5.literal(true)
});

// src/demo.ts
import { z as z6 } from "zod";
var DemoRequestSchema = z6.object({
  /** Prospect's email address. Personal/free domains are allowed. */
  email: z6.string().email(),
  /**
   * Honeypot field for bot detection. Must be absent or empty — any
   * non-empty value indicates an automated submission and the request
   * is silently accepted without provisioning anything.
   */
  honeypot: z6.string().max(0).optional()
}).strict();

// src/email-suppression.ts
import { z as z7 } from "zod";
var SuppressionStatusSchema = z7.enum(["hard_bounce", "soft_bounce", "complaint", "unsubscribed", "manual"]);
var SuppressionConfigurationSetSchema = z7.enum(["cognito-transactional", "application", "manual"]);
var SuppressionScopeSchema = z7.string().refine(
  (value) => {
    if (value === "GLOBAL") return true;
    if (!value.startsWith("T#")) return false;
    const rest = value.slice(2);
    const hashIndex = rest.indexOf("#");
    if (hashIndex <= 0) return false;
    const tenantId = rest.slice(0, hashIndex);
    const emailType = rest.slice(hashIndex + 1);
    return tenantId.length > 0 && emailType.length > 0;
  },
  { message: 'scope must be "GLOBAL" or "T#<tenantId>#<emailType>" with non-empty parts' }
);
var SuppressedEmailSchema = z7.object({
  /**
   * Normalised lowercase email address. Partition key.
   */
  email: nonEmptyString,
  /**
   * Suppression scope. Sort key. See {@link SuppressionScopeSchema}.
   */
  scope: SuppressionScopeSchema,
  /**
   * Current suppression status. Soft bounces can escalate to hard bounces
   * once `bounceCount` reaches the threshold (default 5).
   */
  status: SuppressionStatusSchema,
  /**
   * ISO-8601 timestamp of the most recent event that updated this row.
   */
  bouncedAt: isoDate,
  /**
   * Monotonically increasing count of soft-bounce events received.
   * Used to escalate to `hard_bounce` once the threshold is reached.
   * Hard bounces and complaints write this as 0 on first insert.
   */
  bounceCount: z7.number().int().min(0),
  /**
   * SES-provided reason/description, or a short human-readable reason
   * for operator-added rows.
   */
  reason: z7.string().optional(),
  /**
   * Value of the `X-Accedo-TenantId` header attached to the offending
   * send, when available.
   */
  sourceTenantId: z7.string().optional(),
  /**
   * Value of the `X-Accedo-EmailType` header attached to the offending
   * send, when available.
   */
  sourceEmailType: z7.string().optional(),
  /**
   * Configuration set that produced the suppression event.
   */
  configurationSet: SuppressionConfigurationSetSchema
});

// src/entitlements.ts
import { z as z8 } from "zod";
var TierSchema = z8.enum(["free", "standard", "premium"]);
var AutomatedReminderCadenceStepSchema = z8.object({
  /** Days after the assignment date at which this reminder step fires. Positive integer. */
  delayDays: z8.number().int().min(1),
  /** Whether the signer's manager (User.managerId) is CC'd on this step's reminder email. */
  includeManager: z8.boolean()
});
var DEFAULT_CADENCE = [
  { delayDays: 3, includeManager: false },
  { delayDays: 7, includeManager: false },
  { delayDays: 14, includeManager: true }
];
var TenantFeaturesSchema = z8.object({
  maxAcknowledgmentsPerPeriod: z8.number().int().min(0).nullable(),
  maxActivePolicies: z8.number().int().min(0).nullable(),
  maxAdminUsers: z8.number().int().min(0).nullable(),
  /** Maximum total users allowed for the tenant. Null means unlimited. */
  maxTotalUsers: z8.number().int().min(0).nullable(),
  groupsEnabled: z8.boolean(),
  recurrenceEnabled: z8.boolean(),
  ssoEnabled: z8.boolean(),
  scheduledRemindersEnabled: z8.boolean(),
  analyticsEnabled: z8.boolean(),
  auditLogEnabled: z8.boolean(),
  dataRetentionDays: z8.number().int().min(0).nullable(),
  accountIsolation: z8.boolean(),
  adminDigestEnabled: z8.boolean(),
  templatesEnabled: z8.boolean(),
  /** Whether policy document versioning is enabled for the tenant. */
  versioningEnabled: z8.boolean(),
  /**
   * Whether SCIM (System for Cross-domain Identity Management) is enabled
   * for the tenant (#2240). When true, tenant admins can mint long-lived
   * bearer tokens used by their IdP (Okta, Azure AD, etc.) to provision
   * users via SCIM 2.0. Available on Standard + Premium tiers; gated off
   * for Free.
   *
   * Optional in the schema so pre-existing tenant entitlements records
   * persisted before #2240 — which don't carry the field — continue to
   * parse without a Zod failure, and so the dozens of pre-existing UI
   * test fixtures across the codebase don't all have to be updated.
   * Callers MUST gate on explicit `=== true`; absent is treated as
   * "off". DEFAULT_FEATURES always supplies an explicit value.
   */
  scimEnabled: z8.boolean().optional(),
  /**
   * Whether AI features (AI-suggested policy titles in bulk upload, #2772)
   * are available to the tenant. Available on Standard + Premium tiers;
   * gated off for Free. Documents are processed via AWS Bedrock within
   * Accedo's AWS infrastructure — never shared with the model provider and
   * never used to train models.
   *
   * Optional in the schema so pre-existing entitlements records — which
   * don't carry the field — continue to parse. Unlike `scimEnabled`,
   * consumers gate with a TIER-DEFAULT fallback
   * (`features.aiFeaturesEnabled ?? DEFAULT_FEATURES[tier].aiFeaturesEnabled`)
   * so legacy Standard/Premium rows get the feature without a backfill
   * (#2204 drift pattern). The separate per-tenant opt-out lives on
   * `TenantConfiguration.aiProcessingEnabled`.
   */
  aiFeaturesEnabled: z8.boolean().optional(),
  /**
   * Per-tenant automated-reminder cadence (#2497). When present, the daily
   * scheduled reminder Lambda fires escalating reminders for still-pending
   * assignments at each step's `delayDays`. Standard + Premium tiers default
   * to `DEFAULT_CADENCE` (day 3 / 7 / 14, manager CC on day 14); Free is left
   * undefined so no automated reminders fire (Free keeps the manual
   * "Send Reminder" affordance).
   *
   * Optional in the schema so pre-existing tenant entitlements records
   * persisted before #2497 — which don't carry the field — continue to parse.
   * The Lambda treats an absent value on a scheduled-reminders-enabled tenant
   * as `DEFAULT_CADENCE` (no backfill needed). Stored as a configurable array
   * so v2 (admin-edits-own-cadence) needs no migration.
   */
  automatedReminderCadence: z8.array(AutomatedReminderCadenceStepSchema).optional()
});
var TenantEntitlementsSchema = z8.object({
  tenantId: z8.string().min(1),
  tier: TierSchema,
  features: TenantFeaturesSchema,
  currentPeriodAcknowledgments: z8.number().int().min(0),
  periodStartDate: z8.string().min(1),
  /**
   * Number of "User Pack" add-on units the tenant has purchased (#1878).
   * Each pack lifts `features.maxTotalUsers` by 50. When omitted, the
   * tenant's `maxTotalUsers` is the base cap for its tier. The webhook
   * handler is the canonical writer; the value is reconciled from the
   * `customer.subscription.updated` event payload (Stripe is the source
   * of truth).
   */
  userPackQuantity: z8.number().int().min(0).optional(),
  /**
   * ISO-8601 UTC timestamp of the last time the over-provisioned-packs
   * notifier emailed this tenant's billing admin (#1881). The notifier
   * uses the `YYYY-MM` prefix of this value to enforce one email per
   * tenant per calendar month — a non-empty value whose month matches
   * the current event time short-circuits the run before counting users
   * or sending email.
   *
   * TM-internal field: NOT replicated to sub-accounts. The replicated
   * schema (`TenantEntitlementsReplicatedSchema`) deliberately omits it
   * to keep the cross-account wire format stable.
   */
  lastOverProvisionedNotifiedAt: z8.string().optional(),
  /**
   * Whether the tenant has an established billing account (Stripe customer)
   * in the TM account (#2070). Set to `true` by the Stripe webhook handlers
   * when a checkout session completes or a subscription is reconciled —
   * never cleared (the Stripe customer record survives subscription
   * cancellation, so the boolean remains true). Absent means the tenant has
   * no Stripe customer (treated as `false` by consumers — e.g., legacy
   * tenants that pre-date the self-serve flow or were manually promoted
   * via DDB). Replicated to sub-accounts so the UI can gate the
   * Manage Billing button on it.
   */
  hasBillingAccount: z8.boolean().optional()
});
var TenantEntitlementsReplicatedSchema = TenantEntitlementsSchema.pick({
  tenantId: true,
  tier: true,
  features: true,
  currentPeriodAcknowledgments: true,
  periodStartDate: true,
  // Replicated to sub-accounts so admin UI in each sub-account can show
  // pack quantity alongside the base cap. Optional — absent means "no
  // packs purchased; tenant is on the tier's base cap" (#1878).
  userPackQuantity: true,
  // Replicated to sub-accounts so the UI can gate the Manage Billing
  // button on whether a Stripe customer exists. Optional — absent means
  // "no Stripe customer" (#2070).
  hasBillingAccount: true
}).extend({
  replicaUpdatedAt: z8.string().min(1)
});
var TenantEntitlementsCreatedEventDetailSchema = TenantEntitlementsReplicatedSchema;
var TenantEntitlementsUpdatedEventDetailSchema = TenantEntitlementsReplicatedSchema;
var TenantEntitlementsDeletedEventDetailSchema = TenantEntitlementsSchema.pick({ tenantId: true });
var TenantEntitlementsPeriodResetEventDetailSchema = z8.object({
  tenantId: z8.string().min(1),
  currentPeriodAcknowledgments: z8.literal(0),
  periodStartDate: z8.string().min(1),
  replicaUpdatedAt: z8.string().min(1)
});
var STANDARD_BASE_USERS = 200;
var USERS_PER_PACK = 50;
var DEFAULT_FEATURES = {
  free: {
    maxAcknowledgmentsPerPeriod: null,
    maxActivePolicies: 10,
    maxAdminUsers: 1,
    maxTotalUsers: 25,
    groupsEnabled: false,
    recurrenceEnabled: false,
    ssoEnabled: false,
    scheduledRemindersEnabled: false,
    analyticsEnabled: false,
    auditLogEnabled: false,
    dataRetentionDays: 365,
    accountIsolation: false,
    adminDigestEnabled: false,
    templatesEnabled: false,
    versioningEnabled: false,
    scimEnabled: false,
    aiFeaturesEnabled: false
  },
  standard: {
    // Unlimited per plan/pricing.md ("no per-acknowledgment caps on any tier";
    // Free's natural ceiling is enforced by user/policy caps, not an ack counter).
    // Counter is retained for analytics, just not capped (#1749).
    maxAcknowledgmentsPerPeriod: null,
    maxActivePolicies: 50,
    maxAdminUsers: 5,
    // 200-user base cap per plan/pricing.md (lowered from 500 in #1828).
    // #1630 will add userPackQuantity to lift this in +50 increments.
    // Sourced from STANDARD_BASE_USERS so the literal cannot drift; see
    // the constant's JSDoc (and #2402) for the rationale.
    maxTotalUsers: STANDARD_BASE_USERS,
    groupsEnabled: true,
    recurrenceEnabled: true,
    ssoEnabled: true,
    scheduledRemindersEnabled: true,
    analyticsEnabled: true,
    auditLogEnabled: true,
    dataRetentionDays: 2555,
    accountIsolation: false,
    adminDigestEnabled: true,
    templatesEnabled: true,
    versioningEnabled: true,
    scimEnabled: true,
    aiFeaturesEnabled: true,
    // Automated escalating reminders (#2497): day 3 / 7 / 14, manager CC
    // on day 14. Spread to a mutable array since DEFAULT_CADENCE is readonly.
    automatedReminderCadence: [...DEFAULT_CADENCE]
  },
  premium: {
    maxAcknowledgmentsPerPeriod: null,
    maxActivePolicies: null,
    maxAdminUsers: null,
    maxTotalUsers: null,
    groupsEnabled: true,
    recurrenceEnabled: true,
    ssoEnabled: true,
    scheduledRemindersEnabled: true,
    analyticsEnabled: true,
    auditLogEnabled: true,
    // 10-year default. Premium retention is configurable per tenant — operators
    // may set any positive integer or null (indefinite) on a case-by-case basis.
    // See `plan/pricing.md` and issue #1153.
    dataRetentionDays: 3650,
    accountIsolation: true,
    adminDigestEnabled: true,
    templatesEnabled: true,
    versioningEnabled: true,
    scimEnabled: true,
    aiFeaturesEnabled: true,
    // Automated escalating reminders (#2497): day 3 / 7 / 14, manager CC
    // on day 14. Spread to a mutable array since DEFAULT_CADENCE is readonly.
    automatedReminderCadence: [...DEFAULT_CADENCE]
  }
};

// src/esign-consent.ts
var ESIGN_CONSENT_VERSION = "2026-05-04";
var ESIGN_CONSENT_TEXT = "I consent to signing this policy electronically and understand that my electronic signature is legally binding.";
var ESIGN_INTENT_TEXT = "By clicking below, I confirm I have read, understood, and agree to comply with this policy. My electronic signature has the same legal effect as my handwritten signature.";
var PRE_CONSENT_VERSION_SENTINEL = "pre-consent-v1";

// src/events/event-contracts.ts
var EVENT_SOURCE = Object.freeze({
  /** Tenant service — emits `Tenant*`, `TenantConfiguration*`, `TenantEntitlements*`, and (Stripe-webhook) `AdminAction` events. */
  TENANT: "policy-tracker.tenant-service",
  /** User service — emits `User Created` / `User Updated` / `User Deleted`, `GroupMembersAdded`, and `AdminAction` events. */
  USER: "policy-tracker.user-service",
  /** Policy service — emits `PolicyAcknowledged`, `AssignmentDeclined`, `Policy Deleted`, and `AdminAction` events. */
  POLICY: "policy-tracker.policy-service",
  /** Support service — emits `AdminAction` events for the support-ticket lifecycle. */
  SUPPORT: "policy-tracker.support-service"
});
var EVENT_DETAIL_TYPE = Object.freeze({
  // Tenant lifecycle (services/tenant/src/handlers/dynamodb-stream/tenant/lambda.ts)
  TenantCreated: "TenantCreated",
  TenantUpdated: "TenantUpdated",
  TenantDeleted: "TenantDeleted",
  // Tenant configuration replication (services/tenant/src/handlers/dynamodb-stream/tenant-configuration/lambda.ts)
  TenantConfigurationChanged: "TenantConfigurationChanged",
  TenantConfigurationDeleted: "TenantConfigurationDeleted",
  // Tenant entitlements (services/tenant/src/handlers/dynamodb-stream/tenant-entitlements/lambda.ts)
  TenantEntitlementsCreated: "TenantEntitlementsCreated",
  TenantEntitlementsUpdated: "TenantEntitlementsUpdated",
  TenantEntitlementsDeleted: "TenantEntitlementsDeleted",
  TenantEntitlementsPeriodReset: "TenantEntitlementsPeriodReset",
  // User lifecycle (services/user/src/handlers/dynamodb-stream/user/lambda.ts)
  // Legacy space-separated forms — preserved verbatim so existing rules keep matching.
  UserCreated: "User Created",
  UserUpdated: "User Updated",
  UserDeleted: "User Deleted",
  // Group membership (services/user/src/services/domain-event-service.ts)
  GroupMembersAdded: "GroupMembersAdded",
  // Policy lifecycle (services/policy/src/services/event-service.ts, services/policy/src/handlers/dynamodb-stream/policy/lambda.ts)
  PolicyAcknowledged: "PolicyAcknowledged",
  AssignmentDeclined: "AssignmentDeclined",
  PolicyDeleted: "Policy Deleted",
  // Signing-request email fan-out (#3437) — emitted by the policy service's
  // assign path so the emails leave the API request path; consumed by
  // policy-go-send-signing-requests.
  SigningRequestsRequested: "SigningRequestsRequested",
  // Cross-service audit (services/{tenant,user,policy,support}/src/services/audit-event-service.ts)
  AdminAction: "AdminAction"
});

// src/tenant.ts
import { z as z10 } from "zod";

// src/user.ts
import { z as z9 } from "zod";
var userTypeEnum = z9.enum(["admin", "signer"]);
var UserStatusEnum = z9.enum(["active", "deactivated"]);
var NotificationPreferencesSchema = z9.object({
  /** Reminder emails sent when an acknowledgment assignment is approaching its due date. */
  ackDueReminders: z9.boolean().optional(),
  /** Notification email when a policy is assigned to the user. */
  policyAssignments: z9.boolean().optional(),
  /** Weekly admin digest summarizing signing activity (admin-role only consumers). */
  weeklyAdminDigest: z9.boolean().optional()
}).strict();
var UserSchema = z9.object({
  userId: ulid,
  tenantId: ulid,
  firstName: z9.string().min(1, "First name is required"),
  lastName: z9.string().min(1, "Last name is required"),
  email: z9.string().email(),
  role: z9.enum(["admin", "editor", "viewer"]),
  userType: userTypeEnum.optional(),
  status: UserStatusEnum.default("active"),
  isTestUser: z9.boolean().optional(),
  /**
   * Marks a user provisioned by the self-serve signer demo (#2765 / #2795).
   *
   * Demo confinement is keyed to the USER, not the tenant: in dev the
   * demo tenant is the integration fixture tenant, so a tenant-level
   * flag would confine every fixture signer (which broke the core-loop
   * e2e — #2795). Only the demo-signing-provision Lambda sets this;
   * it is omitted from `CreateUserSchema`/`UpdateUserSchema` so the
   * admin user APIs cannot set or clear it.
   */
  isDemo: z9.boolean().optional(),
  notificationPreferences: NotificationPreferencesSchema.optional(),
  /**
   * External identifier supplied by an IdP provisioning client (#2241 — SCIM phase 3).
   *
   * Populated when the user row is created via `POST /scim/v2/Users` from
   * the SCIM service-provider endpoint. Stored verbatim on the user row
   * so subsequent SCIM operations can correlate Accedo's internal ULID
   * with the upstream IdP's identifier. Optional because admin-API
   * created users (the existing path) have no IdP origin.
   */
  externalId: z9.string().min(1).optional(),
  /**
   * ULID of this user's manager (#2497). When set, the automated
   * escalating-reminder Lambda CCs the manager's email on cadence steps
   * flagged `includeManager` (the day-14 escalation in the v1 cadence).
   * Settable via the user update API (`PATCH /users/:id`); v1 ships no
   * dedicated UI control for it. Optional — most users have no manager
   * recorded, in which case manager-CC steps fall back to signer-only.
   */
  managerId: ulid.optional(),
  /**
   * Latest-known IANA time zone for this user (#2913), e.g.
   * `America/Chicago`. Captured from the browser at the moment of a
   * sign/decline action and used to render email timestamps in the
   * user's local time. Optional — absent until the user first signs or
   * declines something; emails then fall back to UTC (explicitly
   * labeled). Only stored when `Intl.DateTimeFormat` resolves it
   * server-side; invalid strings are dropped, never an error.
   */
  timezone: z9.string().min(1).max(64).optional(),
  createdAt: isoDate
}).strict();
var CreateUserSchema = UserSchema.omit({ userId: true, tenantId: true, createdAt: true, status: true, isDemo: true });
var CreateInitialUserSchema = CreateUserSchema.omit({ role: true });
var UpdateUserSchema = UserSchema.partial().omit({ userId: true, tenantId: true, createdAt: true, status: true, isTestUser: true, isDemo: true });
var UpdateMeSchema = UserSchema.pick({
  firstName: true,
  lastName: true,
  notificationPreferences: true
}).partial().strict();
var UserDataSchema = UserSchema.omit({ userId: true, tenantId: true, createdAt: true });
var UserCreatedEventDetailSchema = UserSchema;
var UserUpdatedEventDetailSchema = z9.object({
  userId: ulid,
  tenantId: ulid,
  createdAt: isoDate,
  oldUser: UserDataSchema,
  newUser: UserDataSchema
});
var UserDeletedEventDetailSchema = UserSchema;
var BulkCreateUserRowSchema = z9.object({
  firstName: z9.string().min(1).max(100),
  lastName: z9.string().min(1).max(100),
  email: z9.string().email(),
  role: z9.enum(["admin", "signer"]).optional().default("signer")
});
var BulkCreateUsersRequestSchema = z9.object({
  users: z9.array(BulkCreateUserRowSchema).min(1).max(100)
});

// src/tenant.ts
var TenantSchema = z10.strictObject({
  tenantId: ulid,
  name: nonEmptyString,
  subDomain,
  awsAccount,
  region,
  createdAt: isoDate,
  initialUser: CreateUserSchema.omit({ role: true }).optional(),
  /**
   * ISO 8601 timestamp at which the registering user accepted the
   * Terms of Service at signup. Optional on the underlying tenant
   * record because tenants created before #1136 — and tenants created
   * by internal/admin paths that do not flow through self-service
   * signup — never had these fields. Required at the public
   * SelfServiceSignupSchema boundary.
   */
  termsAcceptedAt: isoDate.optional(),
  /**
   * Identifier for the version of the Terms of Service the user
   * accepted (e.g. `2026-05-13`). Each published revision of the ToS
   * gets its own string so the audit trail can prove which version a
   * tenant accepted.
   */
  termsVersion: nonEmptyString.optional(),
  /**
   * Stripe Customer ID for this tenant (e.g. `cus_…`). Populated
   * asynchronously after tenant creation by the
   * `stripe-customer-create` EventBridge consumer (issue #1877). May
   * also be populated on demand when the admin clicks "Upgrade Plan"
   * if no async customer was created (e.g. admin-created tenants
   * without an `initialUser`). Optional because tenants created
   * before #1877 do not have one.
   */
  stripeCustomerId: z10.string().optional(),
  /**
   * Stripe Subscription ID for this tenant (e.g. `sub_…`). Persisted
   * by the `checkout.session.completed` webhook branch when an admin
   * completes the Free → Standard upgrade (issue #1877). Optional
   * because free-tier tenants never have one.
   */
  stripeSubscriptionId: z10.string().optional()
});
var CreateTenantSchema = TenantSchema.omit({ tenantId: true, createdAt: true }).merge(z10.strictObject({ initialUser: CreateUserSchema.omit({ role: true }) })).merge(z10.object({ tier: TierSchema.optional() }));
var UpdateTenantSchema = TenantSchema.partial().omit({
  tenantId: true,
  subDomain: true,
  awsAccount: true,
  region: true,
  createdAt: true,
  initialUser: true
});
var TenantDataSchema = TenantSchema.omit({ tenantId: true, createdAt: true });
var TenantCreatedEventDetailSchema = TenantSchema;
var TenantUpdatedEventDetailSchema = z10.object({
  tenantId: ulid,
  createdAt: isoDate,
  oldTenant: TenantDataSchema,
  newTenant: TenantDataSchema
});
var TenantDeletedEventDetailSchema = TenantSchema;
var DigestFrequencySchema = z10.enum(["daily", "weekly", "disabled"]);
var SamlMetadataSchema = z10.discriminatedUnion("source", [
  z10.object({
    source: z10.literal("url"),
    metadataUrl: z10.string().url()
  }),
  z10.object({
    source: z10.literal("xml"),
    metadataXml: z10.string().min(1)
  })
]);
var TenantConfigurationSchema = z10.object({
  tenantId: ulid,
  subDomain,
  awsAccount,
  region,
  userPoolArn: z10.string(),
  userPoolId: z10.string(),
  userPoolClientId: z10.string(),
  audience: z10.string(),
  issuer: z10.string(),
  digestFrequency: DigestFrequencySchema.optional(),
  /**
   * Per-tenant AI-processing opt-out (#2772). When `false`, no document
   * content is ever sent to AWS Bedrock — AI features (AI-suggested policy
   * titles in bulk upload) behave exactly as on the Free tier
   * (filename-derived titles, no model call). Absent means AI processing is
   * ON (the default for Standard+ tenants whose tier enables
   * `aiFeaturesEnabled`). Written locally in the sub-account by the policy
   * service settings endpoint, mirroring the `digestFrequency` pattern.
   */
  aiProcessingEnabled: z10.boolean().optional(),
  /**
   * SAML 2.0 IdP metadata configured for the tenant's SSO integration
   * (#1983, extended to a discriminated union in #2249). When present, the
   * per-tenant CFN stack provisions a Cognito `UserPoolIdentityProvider`
   * (SAML) wired to either the metadata URL (Cognito fetches the document
   * itself) or the metadata XML pasted by the admin (Google Workspace path).
   * Optional on the underlying record because most tenants never enable SSO.
   */
  samlMetadata: SamlMetadataSchema.optional(),
  /**
   * @deprecated #2249 — Legacy single-field SSO configuration superseded by
   * the `samlMetadata` discriminated union. Rows persisted before #2249 may
   * carry this field; readers should normalise it into
   * `{ source: 'url', metadataUrl }` for callers. The
   * `scripts/operator/backfill-saml-metadata-shape.ts` one-shot rewrites legacy rows
   * into the new shape, after which this field can be removed in a future
   * change.
   */
  samlMetadataUrl: z10.string().url().optional(),
  /**
   * When true, the LoginPage auto-redirects to the configured SAML IdP on
   * mount and hides the password/magic-link form. Requires samlMetadata to
   * be configured; the API rejects `ssoOnly: true` with 400 when no SAML
   * metadata is present on the row or in the request body (#2298).
   *
   * Stored as an explicit boolean rather than a presence flag so an admin
   * can flip the toggle off without clearing the metadata, leaving SAML
   * federation available but no longer required.
   */
  ssoOnly: z10.boolean().optional(),
  /**
   * When true, this tenant is the self-serve signer-demo tenant (#2765).
   * Server-computed by the sub-account tenant-configuration handler by
   * comparing the tenant id against the operator-provisioned demo tenant
   * id in SSM — it is never a per-real-tenant stored value. The signer SPA
   * reads this flag to render the confined demo experience (no Signer
   * Portal, terminal signup CTA). Defaults to `false` so every real tenant
   * is unaffected.
   */
  isDemo: z10.boolean().default(false)
});
function getSamlMetadata(config) {
  if (!config) return void 0;
  if (config.samlMetadata) return config.samlMetadata;
  if (config.samlMetadataUrl) return { source: "url", metadataUrl: config.samlMetadataUrl };
  return void 0;
}
var TenantConfigurationChangedEventDetailSchema = TenantConfigurationSchema;
var TenantConfigurationDeletedEventDetailSchema = z10.object({
  tenantId: ulid
});

// src/group.ts
import { z as z11 } from "zod";
var GroupSchema = z11.object({
  groupId: ulid,
  tenantId: ulid,
  name: z11.string().min(1, "Name is required").max(100, "Name must be 100 characters or fewer"),
  createdAt: isoDate,
  archived: z11.boolean().optional()
});
var CreateGroupSchema = z11.object({
  name: z11.string().min(1, "Name is required").max(100, "Name must be 100 characters or fewer")
});
var UpdateGroupSchema = z11.object({
  name: z11.string().min(1, "Name is required").max(100, "Name must be 100 characters or fewer")
});
var GroupMembershipSchema = z11.object({
  groupId: ulid,
  userId: z11.string().min(1),
  tenantId: ulid,
  createdAt: isoDate
});
var UpdateGroupMembersSchema = z11.object({
  userIds: z11.array(z11.string().min(1)).min(1, "At least one user ID is required")
});
var GroupWithMembersSchema = GroupSchema.extend({
  members: z11.array(z11.string())
});
var GroupWithMemberCountSchema = GroupSchema.extend({
  memberCount: z11.number().int().min(0)
});
var AddMembersFailureSchema = z11.object({
  userId: z11.string().min(1),
  error: z11.string().min(1)
});
var AddMembersResultSchema = z11.object({
  added: z11.array(z11.string()),
  failed: z11.array(AddMembersFailureSchema)
});
var GroupPolicySummarySchema = z11.object({
  policyId: ulid,
  title: z11.string()
});
var GroupMembersAddedEventDetailSchema = z11.object({
  tenantId: ulid,
  groupId: ulid,
  userIds: z11.array(z11.string().min(1)).min(1),
  actorId: z11.string().min(1)
});

// src/policy.ts
import { z as z12 } from "zod";
var RecurrenceTypeEnum = z12.enum(["annual", "quarterly", "semi-annual"]).nullable();
var PolicyTagsSchema = z12.array(
  z12.string().min(1).max(50).transform((s) => s.toLowerCase())
).max(10).optional();
var PolicyVersionSchema = z12.object({
  /** Sequential version number starting at 1. */
  version: z12.number().int().min(1),
  /** S3 key for this version's document (e.g. tenantId/policyId/v1/file.pdf). */
  s3Key: z12.string().min(1),
  /** ISO-8601 timestamp when this version was uploaded. */
  uploadedAt: isoDate,
  /** User ID of the person who uploaded this version. */
  uploadedBy: z12.string().min(1),
  /** Optional description of what changed in this version (max 500 chars). */
  changeNote: z12.string().max(500).optional(),
  /** S3 object VersionId — non-repudiation forensic id, #1144/#2592. */
  documentVersionId: z12.string().min(1).optional()
});
var UploadNewVersionRequestSchema = z12.object({
  /** Optional description of what changed in this version (max 500 chars). */
  changeNote: z12.string().max(500).optional()
});
var PolicySchema = z12.object({
  policyId: ulid,
  tenantId: ulid,
  title: z12.string().min(1, "Title is required").max(255),
  status: z12.enum(["active"]),
  s3Key: z12.string().min(1),
  createdAt: isoDate,
  createdBy: z12.string().min(1),
  recurrenceType: RecurrenceTypeEnum.optional(),
  minViewTimeSeconds: z12.number().int().min(0).optional(),
  pageCount: z12.number().int().min(1).optional(),
  tags: PolicyTagsSchema,
  /** Current version number. Starts at 1 for new policies. Optional for backward compat. */
  currentVersion: z12.number().int().min(1).optional(),
  /** Full version history. Optional for backward compat with existing records. */
  versions: z12.array(PolicyVersionSchema).optional()
}).strict();
var CreatePolicySchema = z12.object({
  title: z12.string().min(1, "Title is required").max(255),
  recurrenceType: RecurrenceTypeEnum.optional(),
  tags: PolicyTagsSchema
});
var UpdatePolicySchema = z12.object({
  title: z12.string().min(1, "Title is required").max(255).optional(),
  recurrenceType: RecurrenceTypeEnum.optional(),
  tags: PolicyTagsSchema
});
var AssignmentStatusEnum = z12.enum(["pending", "signed", "expired", "revoked", "declined"]);
var BaseAssignmentFields = z12.object({
  assignmentId: ulid,
  tenantId: ulid,
  policyId: ulid,
  userId: z12.string().min(1),
  /**
   * Denormalized snapshot of the assigned user's first name at write
   * time (issue #1773). Populated from the canonical user record when
   * the assignment is created so reader paths (Acknowledgments view,
   * exports, certificates) can render the signer identity even when
   * the underlying user record is later deleted or unreachable.
   * Optional for backward compatibility with rows written before the
   * fix landed; new rows always carry it.
   */
  firstName: z12.string().optional(),
  /** See `firstName`. Snapshot of the user's last name at write time. */
  lastName: z12.string().optional(),
  /** See `firstName`. Snapshot of the user's email at write time. */
  email: z12.string().optional(),
  tokenHash: z12.string().min(1),
  expiresAt: isoDate,
  createdAt: isoDate,
  createdBy: z12.string().min(1),
  reminderCount: z12.number().int().min(0).optional(),
  lastReminderAt: isoDate.optional(),
  /**
   * Idempotency record for the automated escalating-reminder cadence (#2497):
   * the set of cadence `delayDays` values (e.g. `[3, 7]`) for which a reminder
   * has already fired on this assignment. Stored as a DynamoDB Number Set and
   * updated atomically (`ADD`) when a cadence step's email send succeeds. The
   * daily Lambda skips any step whose `delayDays` is already present here, so a
   * re-run never re-sends. Distinct from `reminderCount`, which counts ALL
   * reminders (manual + automated) for cap purposes; `firedReminders` tracks
   * only which automated cadence steps have fired. Optional — absent on rows
   * written before #2497 and on assignments that have never been reminded.
   *
   * Stored as a DynamoDB Number Set, so `@aws-sdk/util-dynamodb` `unmarshall`
   * yields a JS `Set` on read (not an array). The `z.preprocess` below
   * coerces a `Set` to an array before array validation so the production
   * read path (`findAllPendingForEscalatingReminders` → `parseRow`)
   * round-trips correctly; the parsed output type stays `number[]`.
   */
  firedReminders: z12.preprocess((v) => v instanceof Set ? [...v] : v, z12.array(z12.number().int().min(0))).optional(),
  period: z12.string().min(1).optional(),
  dueDate: z12.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  source: z12.enum(["manual", "group-auto-assign"]).optional(),
  sourceGroupId: z12.string().min(1).optional(),
  /** The policy version active when this assignment was created. Optional for backward compat. */
  policyVersion: z12.number().int().min(1).optional(),
  /** S3 object VersionId — non-repudiation forensic id, #1144/#2592. */
  documentVersionId: z12.string().min(1).optional()
});
var PendingAssignmentSchema = BaseAssignmentFields.extend({
  status: z12.literal("pending")
});
var ExpiredAssignmentSchema = BaseAssignmentFields.extend({
  status: z12.literal("expired")
});
var SignedAssignmentSchema = BaseAssignmentFields.extend({
  status: z12.literal("signed"),
  acknowledgedAt: isoDate,
  acknowledgedIp: z12.string().optional(),
  acknowledgedUserAgent: z12.string().optional(),
  /**
   * IANA time zone of the signer's browser at the moment of signing
   * (#2913), e.g. `America/Chicago`. Sign-time provenance parallel to
   * `acknowledgedIp` / `acknowledgedUserAgent`; drives recipient-local
   * timestamp rendering in the acknowledgment confirmation email.
   * Optional — absent on rows signed before capture shipped and when the
   * browser-supplied value failed server-side validation.
   */
  signedTimezone: z12.string().optional(),
  /**
   * Whether the signer affirmatively checked the ESIGN-Act consent
   * checkbox at sign time. Always `true` when set; the field is absent
   * for assignments captured before the explicit consent UI shipped.
   */
  electronicSignatureConsented: z12.boolean().optional(),
  /**
   * Version of the consent text the signer agreed to. Live signers
   * receive `ESIGN_CONSENT_VERSION`; rows backfilled by the migration
   * Lambda receive `PRE_CONSENT_VERSION_SENTINEL`.
   */
  consentVersion: z12.string().min(1).optional()
});
var DeclinedAssignmentSchema = BaseAssignmentFields.extend({
  status: z12.literal("declined"),
  /**
   * ISO-8601 timestamp when the signer declined the assignment.
   * Set together with `status: 'declined'`.
   */
  declinedAt: isoDate,
  /**
   * The userId of the signer who declined. Always equal to the
   * assignment's `userId` — recorded explicitly for audit clarity.
   */
  declinedBy: z12.string().min(1),
  /**
   * Optional free-text reason the signer provided when declining.
   * Capped at 500 characters; absent when the signer chose not to
   * supply one.
   */
  declineReason: z12.string().max(500).optional(),
  /** IP address captured at decline time (sign-time provenance, parallel to `acknowledgedIp`). */
  declinedIp: z12.string().optional(),
  /** User agent captured at decline time (sign-time provenance, parallel to `acknowledgedUserAgent`). */
  declinedUserAgent: z12.string().optional(),
  /**
   * IANA time zone of the signer's browser at the moment of declining
   * (#2913) — decline-side counterpart of `signedTimezone`. Optional;
   * absent on legacy rows and when validation dropped the value.
   */
  declinedTimezone: z12.string().optional()
});
var RevokedAssignmentSchema = BaseAssignmentFields.extend({
  status: z12.literal("revoked"),
  revokedAt: isoDate,
  revokedBy: z12.string().min(1),
  revocationReason: z12.string().max(500).optional()
});
var PolicyAssignmentSchema = z12.discriminatedUnion("status", [
  PendingAssignmentSchema,
  SignedAssignmentSchema,
  DeclinedAssignmentSchema,
  RevokedAssignmentSchema,
  ExpiredAssignmentSchema
]);
var GroupPolicyAssignmentSchema = z12.object({
  tenantId: ulid,
  groupId: ulid,
  policyId: ulid,
  dueDate: z12.string().optional(),
  createdAt: isoDate,
  createdBy: z12.string().min(1)
});
var CreatePolicyAssignmentRequestSchema = z12.object({
  userIds: z12.array(z12.string().min(1)).optional(),
  groupIds: z12.array(z12.string().min(1)).optional(),
  dueDate: z12.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional()
}).refine((data) => (data.userIds?.length ?? 0) > 0 || (data.groupIds?.length ?? 0) > 0, {
  message: "At least one user ID or group ID is required"
});
var AssignmentsDateRangeQuerySchema = z12.object({
  dueDateFrom: z12.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dueDateTo: z12.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional()
});
var RevokeAssignmentRequestSchema = z12.object({ reason: z12.string().max(500).optional() });
var EditAssignmentDueDateRequestSchema = z12.object({
  dueDate: z12.union([z12.string().regex(/^\d{4}-\d{2}-\d{2}$/), z12.null()])
}).strict();
var DeclineAssignmentRequestSchema = z12.object({
  reason: z12.string().max(500).optional(),
  /**
   * IANA time zone of the signer's browser (#2913), e.g.
   * `Intl.DateTimeFormat().resolvedOptions().timeZone`. Optional; the
   * server validates it and silently drops anything `Intl` cannot resolve
   * — a bad value never fails the decline.
   */
  timezone: z12.string().max(64).optional()
});
var BulkRevokeAssignmentsRequestSchema = z12.object({
  assignmentIds: z12.array(z12.string().min(1)).min(1).max(100),
  reason: z12.string().max(500).optional()
});
var AcknowledgePolicyByTokenRequestSchema = z12.object({
  electronicSignatureConsented: z12.literal(true),
  consentVersion: z12.string().min(1)
}).strict();
var AcknowledgePolicyByAuthRequestSchema = z12.object({
  electronicSignatureConsented: z12.literal(true),
  consentVersion: z12.string().min(1),
  /**
   * IANA time zone of the signer's browser at sign time (#2913), e.g.
   * `Intl.DateTimeFormat().resolvedOptions().timeZone`. Optional; the
   * server validates it and silently drops anything `Intl` cannot
   * resolve — a bad value never fails the acknowledgment.
   */
  timezone: z12.string().max(64).optional()
}).strict();
var AcknowledgmentViewItemSchema = z12.object({
  /** The assignment ID for this acknowledgment record. */
  assignmentId: z12.string().min(1),
  userId: z12.string().min(1),
  /**
   * Signer's first name as snapshotted on the assignment row at write
   * time (issue #1773). For preserved evidentiary rows whose user record
   * has since been deleted (#2462), this still resolves from the snapshot
   * — admin views render the historical name verbatim. Empty string only
   * for legacy pre-#1773 rows where neither the snapshot nor the user
   * lookup resolves; the UI then renders the `(Former user)` fallback.
   */
  firstName: z12.string(),
  /** See `firstName`. Snapshot of the signer's last name at write time. */
  lastName: z12.string(),
  /** See `firstName`. Snapshot of the signer's email at write time. */
  email: z12.string(),
  /**
   * `true` when the user-service record for `userId` could not be
   * resolved at read time (issue #2462). The historical identity
   * snapshot on the row (firstName / lastName / email) remains
   * authoritative; this flag exists so the admin UI can mark the row
   * as a former user without re-deriving it from the field shapes.
   */
  userDeleted: z12.boolean().optional(),
  status: z12.enum(["signed", "pending", "revoked", "declined"]),
  acknowledgedAt: z12.string().optional(),
  reminderCount: z12.number().int().min(0).optional(),
  lastReminderAt: z12.string().optional(),
  /** The recurrence period for this assignment (e.g. "2026", "2026-Q1"). Absent for one-time policies. */
  period: z12.string().optional(),
  /** The due date for this assignment (YYYY-MM-DD). Absent when no due date was set. */
  dueDate: z12.string().optional(),
  /** Whether this assignment is overdue (pending and past due date). Always false when signed or no dueDate set. */
  isOverdue: z12.boolean().optional(),
  /** ISO-8601 timestamp when the assignment was revoked. Only present for revoked assignments. */
  revokedAt: z12.string().optional(),
  /** The reason for revoking the assignment. Optional. */
  revocationReason: z12.string().optional(),
  /** The policy version at the time this assignment was created. */
  policyVersion: z12.number().int().min(1).optional(),
  /** ISO-8601 timestamp when the signer declined. Only present for declined assignments. */
  declinedAt: z12.string().optional(),
  /** Optional reason the signer supplied when declining. */
  declineReason: z12.string().optional(),
  /**
   * Sign-time provenance — IP address captured when the signer
   * acknowledged the policy. Surfaced in the admin / signer details
   * view (issue #1147). Absent on pre-capture historical rows and on
   * pending assignments.
   */
  acknowledgedIp: z12.string().optional(),
  /** Sign-time provenance — User-Agent header captured at acknowledgment time. */
  acknowledgedUserAgent: z12.string().optional(),
  /** Whether the signer affirmatively consented to electronic signature at sign time. */
  electronicSignatureConsented: z12.boolean().optional(),
  /** Version of the ESIGN consent text the signer agreed to. */
  consentVersion: z12.string().optional(),
  /** Sign-time provenance — IP address captured when the signer declined. */
  declinedIp: z12.string().optional(),
  /** Sign-time provenance — User-Agent header captured when the signer declined. */
  declinedUserAgent: z12.string().optional()
});
var AcknowledgmentViewSchema = z12.object({
  policyId: z12.string().min(1),
  totalCount: z12.number().int().min(0),
  signedCount: z12.number().int().min(0),
  /** Number of pending assignments that are past their due date. */
  overdueCount: z12.number().int().min(0),
  items: z12.array(AcknowledgmentViewItemSchema)
});
var PolicyAcknowledgedEventDetailSchema = z12.object({
  tenantId: ulid,
  policyId: ulid,
  assignmentId: ulid,
  userId: z12.string().min(1),
  policyTitle: z12.string().min(1),
  acknowledgedAt: isoDate,
  /** S3 key for the generated acknowledgment receipt PDF, if available. */
  receiptS3Key: z12.string().min(1).optional(),
  /**
   * IANA time zone captured from the signer's browser at the moment of
   * signing (#2913). Lets the acknowledgment-confirmation email render
   * `acknowledgedAt` in the signer's local time. Optional — consumers
   * fall back to the user record's `timezone`, then to labeled UTC.
   */
  signedTimezone: z12.string().min(1).optional()
});
var AssignmentDeclinedEventDetailSchema = z12.object({
  tenantId: ulid,
  policyId: ulid,
  assignmentId: ulid,
  userId: z12.string().min(1),
  policyTitle: z12.string().min(1),
  declinedAt: isoDate,
  declineReason: z12.string().max(500).optional(),
  signerName: z12.string().min(1).optional(),
  signerEmail: z12.string().min(1).optional()
});
var SigningRequestRecipientSchema = z12.object({
  assignmentId: ulid,
  userId: z12.string().min(1),
  firstName: z12.string(),
  lastName: z12.string(),
  email: z12.string().min(1),
  isTestUser: z12.boolean().optional()
});
var SigningRequestsRequestedEventDetailSchema = z12.object({
  tenantId: ulid,
  policyId: ulid,
  policyTitle: z12.string().min(1),
  /** Recurrence period the assignments were created for; part of their sort key. */
  period: z12.string().min(1),
  dueDate: z12.string().min(1).optional(),
  recipients: z12.array(SigningRequestRecipientSchema).min(1).max(50)
});
var PolicyDeletedEventDetailSchema = z12.object({
  tenantId: ulid,
  policyId: ulid,
  title: z12.string().min(1),
  s3Key: z12.string().min(1),
  /**
   * Every S3 object key associated with the deleted policy — the deduped
   * union of the current top-level `s3Key` and each `versions[].s3Key`.
   * The `handle-policy-deleted` consumer deletes all versions of these
   * keys so deleting a policy does not leave orphaned objects in S3.
   */
  s3Keys: z12.array(z12.string().min(1))
});
var LoginTokenSchema = z12.object({
  tokenHash: z12.string().min(1),
  tenantId: ulid,
  userId: z12.string().min(1),
  expiresAt: isoDate,
  createdAt: isoDate,
  itemType: z12.literal("LOGIN_TOKEN")
}).strict();
var PolicyAcknowledgmentStatsSchema = z12.object({
  totalPolicies: z12.number().int().min(0),
  activePolicies: z12.number().int().min(0),
  pendingAcknowledgments: z12.number().int().min(0),
  overdueAcknowledgments: z12.number().int().min(0),
  completionRate: z12.number().min(0).max(100),
  lastUpdated: z12.string().min(1),
  /**
   * Optional state of {@link activePolicies} at the start of the comparable prior
   * period (`now - rangeDays`). Present only when a finite analytics range is supplied
   * to the stats endpoint. Used by the dashboard to render per-card deltas.
   */
  activePoliciesPriorPeriod: z12.number().int().min(0).optional(),
  /**
   * Optional state of {@link pendingAcknowledgments} at the start of the comparable
   * prior period. See {@link activePoliciesPriorPeriod}.
   */
  pendingAcknowledgmentsPriorPeriod: z12.number().int().min(0).optional(),
  /**
   * Optional state of {@link overdueAcknowledgments} at the start of the comparable
   * prior period. See {@link activePoliciesPriorPeriod}.
   */
  overdueAcknowledgmentsPriorPeriod: z12.number().int().min(0).optional(),
  /**
   * Optional state of {@link completionRate} at the start of the comparable prior
   * period. See {@link activePoliciesPriorPeriod}.
   */
  completionRatePriorPeriod: z12.number().min(0).max(100).optional()
});
var SignerAssignmentSchema = z12.object({
  /** The unique assignment ID (also used as the confirmation ID for completed assignments). */
  assignmentId: z12.string().min(1),
  /** The ID of the policy being assigned. */
  policyId: z12.string().min(1),
  /** The human-readable title of the policy. */
  policyTitle: z12.string().min(1),
  /** Whether the assignment is pending acknowledgment, signed, or declined. */
  status: z12.enum(["pending", "signed", "declined"]),
  /** ISO-8601 timestamp when the assignment was created. */
  createdAt: z12.string().min(1),
  /** Optional due date (YYYY-MM-DD). */
  dueDate: z12.string().optional(),
  /** ISO-8601 timestamp when the policy was acknowledged. Only present for completed assignments. */
  acknowledgedAt: z12.string().optional(),
  /** Whether this pending assignment is past its due date. */
  isOverdue: z12.boolean().optional(),
  /** ISO-8601 timestamp when the signer declined. Only present for declined assignments. */
  declinedAt: z12.string().optional(),
  /** Optional reason the signer supplied when declining. */
  declineReason: z12.string().optional(),
  /**
   * Sign-time provenance — IP address captured when the signer
   * acknowledged the policy. Used by the signer portal "View Details"
   * modal (issue #1147). Absent on pending and pre-capture rows.
   */
  acknowledgedIp: z12.string().optional(),
  /** Sign-time provenance — User-Agent header captured at acknowledgment time. */
  acknowledgedUserAgent: z12.string().optional(),
  /** Whether the signer affirmatively consented to electronic signature at sign time. */
  electronicSignatureConsented: z12.boolean().optional(),
  /** Version of the ESIGN consent text the signer agreed to. */
  consentVersion: z12.string().optional(),
  /** Sign-time provenance — IP address captured when the signer declined. */
  declinedIp: z12.string().optional(),
  /** Sign-time provenance — User-Agent header captured when the signer declined. */
  declinedUserAgent: z12.string().optional()
});
var AnalyticsDateRangeSchema = z12.enum(["7d", "30d", "90d", "all"]);
var CompletionTrendPointSchema = z12.object({
  /** The date for this bucket (YYYY-MM-DD). */
  date: z12.string().min(1),
  /** Completion rate as a percentage (0-100). */
  completionRate: z12.number().min(0).max(100),
  /** Total non-revoked assignments created on or before this date. */
  totalAssignments: z12.number().int().min(0),
  /** Cumulative number of signed assignments acknowledged on or before this date. */
  signedCount: z12.number().int().min(0)
});
var TimeToSignByPolicySchema = z12.object({
  /** The policy ID. */
  policyId: z12.string().min(1),
  /** The human-readable policy title. */
  policyTitle: z12.string().min(1),
  /** Average days from assignment creation to acknowledgment. */
  avgDays: z12.number().min(0),
  /** Number of signed assignments used to compute the average. */
  assignmentCount: z12.number().int().min(0)
});
var OverdueBreakdownSchema = z12.object({
  /** Assignments signed on or before their due date (or signed with no due date). */
  onTime: z12.number().int().min(0),
  /** Pending assignments that are past their due date. */
  overdue: z12.number().int().min(0),
  /** Pending assignments not yet past their due date. */
  pending: z12.number().int().min(0),
  /** Revoked assignments. */
  revoked: z12.number().int().min(0),
  /** Declined assignments (issue #1682). Tracked separately from overdue
   *  because a decline is an active refusal, not a missed deadline — admins
   *  triage these differently. */
  declined: z12.number().int().min(0)
});
var DashboardAnalyticsSchema = z12.object({
  /** Completion trend bucketed by day. */
  completionTrend: z12.array(CompletionTrendPointSchema),
  /** Average time-to-sign per policy. */
  timeToSign: z12.array(TimeToSignByPolicySchema),
  /**
   * Number of signed assignments whose policy could not be resolved (e.g. the
   * policy was deleted) and were therefore dropped from `timeToSign`. The UI
   * surfaces this as a "+N hidden" notice on the chart so the cap is never
   * silent — see issue #1694.
   */
  timeToSignHiddenCount: z12.number().int().min(0),
  /** Breakdown of assignments by completion/overdue status. */
  overdueBreakdown: OverdueBreakdownSchema,
  /** The date range used for the analytics query. */
  range: AnalyticsDateRangeSchema
});
var GroupStatsAggregateSchema = z12.object({
  /** The tenant that owns this aggregate record. */
  tenantId: ulid,
  /** The group this aggregate is for. */
  groupId: ulid,
  /** The human-readable name of the group (denormalized for read efficiency). */
  groupName: z12.string().min(1),
  /** Number of signed assignments for members of this group. */
  signedCount: z12.number().int().min(0),
  /** Number of pending assignments for members of this group. */
  pendingCount: z12.number().int().min(0),
  /** Number of overdue (pending + past due date) assignments for members of this group. */
  overdueCount: z12.number().int().min(0),
  /** Total non-revoked assignments for members of this group. */
  totalAssignments: z12.number().int().min(0),
  /** The DynamoDB stream event ID of the last event processed (for deduplication). */
  lastEventId: z12.string().min(1),
  /** ISO-8601 timestamp of when this aggregate was last updated. */
  lastUpdatedAt: isoDate
});
var PolicyStatsAggregateSchema = z12.object({
  /** The tenant that owns this aggregate record. */
  tenantId: ulid,
  /** The policy this aggregate is for. */
  policyId: ulid,
  /** Number of signed assignments for this policy. */
  signedCount: z12.number().int().min(0),
  /** Number of pending assignments for this policy. */
  pendingCount: z12.number().int().min(0),
  /** Number of overdue (pending + past due date) assignments for this policy. */
  overdueCount: z12.number().int().min(0),
  /** Total non-revoked assignments for this policy. */
  totalCount: z12.number().int().min(0),
  /** The DynamoDB stream event ID of the last event processed (for deduplication). */
  lastEventId: z12.string().min(1),
  /** ISO-8601 timestamp of when this aggregate was last updated. */
  lastUpdatedAt: isoDate
});
var GroupCompletionStatsSchema = z12.object({
  /** The unique group ID. */
  groupId: z12.string().min(1),
  /** The human-readable name of the group. */
  groupName: z12.string().min(1),
  /** Total number of policy assignments for members of this group. */
  totalAssignments: z12.number().int().min(0),
  /** Number of assignments that have been signed. */
  signedCount: z12.number().int().min(0),
  /** Number of assignments still pending. */
  pendingCount: z12.number().int().min(0),
  /** Number of pending assignments that are past their due date. */
  overdueCount: z12.number().int().min(0),
  /** Completion rate as a percentage (0-100). */
  completionRate: z12.number().min(0).max(100)
});

// src/events/event-schemas.ts
var EVENT_SCHEMAS = {
  // Tenant lifecycle
  [EVENT_DETAIL_TYPE.TenantCreated]: TenantCreatedEventDetailSchema,
  [EVENT_DETAIL_TYPE.TenantUpdated]: TenantUpdatedEventDetailSchema,
  [EVENT_DETAIL_TYPE.TenantDeleted]: TenantDeletedEventDetailSchema,
  // Tenant configuration replication
  [EVENT_DETAIL_TYPE.TenantConfigurationChanged]: TenantConfigurationChangedEventDetailSchema,
  [EVENT_DETAIL_TYPE.TenantConfigurationDeleted]: TenantConfigurationDeletedEventDetailSchema,
  // Tenant entitlements
  [EVENT_DETAIL_TYPE.TenantEntitlementsCreated]: TenantEntitlementsCreatedEventDetailSchema,
  [EVENT_DETAIL_TYPE.TenantEntitlementsUpdated]: TenantEntitlementsUpdatedEventDetailSchema,
  [EVENT_DETAIL_TYPE.TenantEntitlementsDeleted]: TenantEntitlementsDeletedEventDetailSchema,
  [EVENT_DETAIL_TYPE.TenantEntitlementsPeriodReset]: TenantEntitlementsPeriodResetEventDetailSchema,
  // User lifecycle
  [EVENT_DETAIL_TYPE.UserCreated]: UserCreatedEventDetailSchema,
  [EVENT_DETAIL_TYPE.UserUpdated]: UserUpdatedEventDetailSchema,
  [EVENT_DETAIL_TYPE.UserDeleted]: UserDeletedEventDetailSchema,
  // Group membership
  [EVENT_DETAIL_TYPE.GroupMembersAdded]: GroupMembersAddedEventDetailSchema,
  // Policy lifecycle
  [EVENT_DETAIL_TYPE.PolicyAcknowledged]: PolicyAcknowledgedEventDetailSchema,
  [EVENT_DETAIL_TYPE.AssignmentDeclined]: AssignmentDeclinedEventDetailSchema,
  [EVENT_DETAIL_TYPE.PolicyDeleted]: PolicyDeletedEventDetailSchema,
  [EVENT_DETAIL_TYPE.SigningRequestsRequested]: SigningRequestsRequestedEventDetailSchema,
  // Cross-service audit
  [EVENT_DETAIL_TYPE.AdminAction]: AdminActionEventDetailSchema
};

// src/events/event-helpers.ts
async function emitEvent(args) {
  const parsed = args.schema.parse(args.detail);
  const command = new args.commandCtor({
    Entries: [
      {
        Source: args.source,
        DetailType: args.detailType,
        Detail: JSON.stringify(parsed),
        EventBusName: args.eventBusName
      }
    ]
  });
  const result = await args.client.send(command);
  if (result.FailedEntryCount && result.FailedEntryCount > 0) {
    const firstError = result.Entries?.find((entry) => entry.ErrorCode);
    throw new Error(
      `EventBridge PutEvents reported ${result.FailedEntryCount} failed entr${result.FailedEntryCount === 1 ? "y" : "ies"}: ${firstError?.ErrorMessage ?? "unknown error"}`
    );
  }
}
async function emitEvents(args) {
  const parsedEntries = args.entries.map((entry) => ({
    Source: entry.source,
    DetailType: entry.detailType,
    Detail: JSON.stringify(entry.schema.parse(entry.detail)),
    EventBusName: args.eventBusName
  }));
  const command = new args.commandCtor({ Entries: parsedEntries });
  const result = await args.client.send(command);
  if (result.FailedEntryCount && result.FailedEntryCount > 0) {
    const firstError = result.Entries?.find((entry) => entry.ErrorCode);
    throw new Error(
      `EventBridge PutEvents reported ${result.FailedEntryCount} failed entr${result.FailedEntryCount === 1 ? "y" : "ies"}: ${firstError?.ErrorMessage ?? "unknown error"}`
    );
  }
}
function validateEvent(detail, schema) {
  const candidate = schema;
  const parseable = typeof candidate.passthrough === "function" ? candidate.passthrough() : schema;
  const result = parseable.safeParse(detail);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

// src/resend.ts
import { z as z13 } from "zod";
var ResendWebhookEventTypeSchema = z13.enum([
  "email.sent",
  "email.delivered",
  "email.delivery_delayed",
  "email.complained",
  "email.bounced",
  "email.opened",
  "email.clicked"
]);
var ResendBounceSchema = z13.object({
  type: z13.string().optional(),
  subType: z13.string().optional(),
  message: z13.string().optional()
});
var baseDataShape = {
  /** Resend's id for the underlying email. Used for log correlation. */
  email_id: z13.string().optional(),
  /** Recipient(s). Resend sends a string for a single recipient or an array for many. */
  to: z13.union([z13.string(), z13.array(z13.string())]),
  /** Sender address, when present. */
  from: z13.string().optional(),
  /** Subject line, when present. */
  subject: z13.string().optional()
};
var ResendEmailBouncedSchema = z13.object({
  type: z13.literal("email.bounced"),
  created_at: z13.string(),
  data: z13.object({
    ...baseDataShape,
    /** Bounce classification — present on hard and soft bounces. */
    bounce: ResendBounceSchema.optional()
  })
});
function informationalSchema(type) {
  return z13.object({
    type: z13.literal(type),
    created_at: z13.string(),
    data: z13.object(baseDataShape)
  });
}
var ResendWebhookEventSchema = z13.discriminatedUnion("type", [
  ResendEmailBouncedSchema,
  informationalSchema("email.complained"),
  informationalSchema("email.sent"),
  informationalSchema("email.delivered"),
  informationalSchema("email.delivery_delayed"),
  informationalSchema("email.opened"),
  informationalSchema("email.clicked")
]);
function parseResendWebhookEvent(raw) {
  return ResendWebhookEventSchema.parse(raw);
}
function normalizeRecipients(to) {
  return Array.isArray(to) ? to : [to];
}

// src/scim-token.ts
import { z as z14 } from "zod";
var TokenIdSchema = z14.string().length(26).regex(/^[0-9A-HJKMNP-TV-Z]{26}$/);
var TokenHashSchema = z14.string().length(64).regex(/^[0-9a-f]{64}$/);
var TokenNameSchema = z14.string().trim().min(1, "name must not be empty").max(100, "name must be 100 characters or fewer");
var ScimTokenRecordSchema = z14.object({
  tokenHash: TokenHashSchema,
  tenantId: z14.string().length(26).regex(/^[0-9A-HJKMNP-TV-Z]{26}$/),
  tokenId: TokenIdSchema,
  name: TokenNameSchema,
  createdAt: z14.string().datetime(),
  createdByUserId: z14.string().length(26).regex(/^[0-9A-HJKMNP-TV-Z]{26}$/),
  lastUsedAt: z14.string().datetime().optional(),
  revokedAt: z14.string().datetime().optional()
});
var ScimTokenSummarySchema = z14.object({
  tokenId: TokenIdSchema,
  name: TokenNameSchema,
  createdAt: z14.string().datetime(),
  lastUsedAt: z14.string().datetime().optional(),
  revokedAt: z14.string().datetime().optional()
});
var CreateScimTokenRequestSchema = z14.object({
  name: TokenNameSchema
}).strict();
var CreateScimTokenResponseSchema = z14.object({
  tokenId: TokenIdSchema,
  name: TokenNameSchema,
  createdAt: z14.string().datetime(),
  /** Plaintext token in the form `scim_<32-byte-base64url-random>`.
      Returned only on creation; the user must copy it immediately. */
  token: z14.string().regex(/^scim_[A-Za-z0-9_-]{43}$/)
});

// src/signer-token-spec.ts
var SIGNER_TOKEN_ISSUER = "https://auth.accedo.com/signer";
var SIGNER_USER_TYPE = "signer";
var SIGNER_JWT_ALG = "RS256";
var SIGNER_KMS_SIGNING_ALGORITHM = "RSASSA_PKCS1_V1_5_SHA_256";
var SIGNER_TOKEN_TTL_SECONDS = 12 * 60 * 60;

// src/signup.ts
import { z as z15 } from "zod";
var SelfServiceSignupSchema = z15.object({
  /** Applicant's first name */
  firstName: z15.string().min(1).max(100),
  /** Applicant's last name */
  lastName: z15.string().min(1).max(100),
  /** Applicant's business email address */
  email: z15.string().email(),
  /** Name of the company or organization */
  companyName: z15.string().min(1).max(100),
  /** Desired subdomain for the tenant (lowercase alphanumeric with hyphens) */
  subDomain,
  /**
   * ISO 8601 timestamp at which the user accepted the Terms of Service
   * (issue #1136). The client captures `new Date().toISOString()` at the
   * moment the form is submitted; the server stores it on the tenant
   * record so the audit trail can prove acceptance.
   */
  termsAcceptedAt: isoDate,
  /**
   * Identifier for the version of the Terms of Service the user
   * accepted (e.g. `2026-05-13`). The server stores this on the
   * tenant record alongside the timestamp.
   */
  termsVersion: nonEmptyString,
  /**
   * Honeypot field for bot detection.
   * Must be absent or empty — any non-empty value indicates automated submission.
   */
  honeypot: z15.string().max(0).optional()
}).strict();

// src/support-ticket.ts
import { z as z16 } from "zod";
var SupportTicketSideEnum = z16.enum(["tenant", "tm"]);
var SupportTicketStatusEnum = z16.enum(["open", "closed"]);
var BaseSupportTicketFields = z16.object({
  tenantId: ulid,
  ticketId: ulid,
  subject: z16.string().min(1).max(200),
  createdByUserId: z16.string().min(1),
  /**
   * Display name shown to other admins. In part 1 of #2303 this is the
   * creator's email (no cross-service user-name lookup); part 2 may swap it
   * for the user's first+last name once the user-directory plumbing is
   * extended.
   */
  createdByName: z16.string().min(1),
  createdAt: isoDate,
  updatedAt: isoDate,
  /**
   * Composite GSI sort-key value: `<status>#<updatedAt>` (e.g.
   * `open#2026-05-22T11:23:01.000Z`). Stored as a plain attribute so writes
   * and queries do not need `ExpressionAttributeNames` on `#`.
   */
  statusUpdatedAt: z16.string().min(1)
});
var OpenTicketSchema = BaseSupportTicketFields.extend({
  status: z16.literal("open")
});
var ClosedTicketSchema = BaseSupportTicketFields.extend({
  status: z16.literal("closed"),
  closedReason: z16.string().min(1).max(500).optional(),
  closedAt: isoDate.optional(),
  closedByUserId: z16.string().min(1).optional(),
  closedBySide: SupportTicketSideEnum.optional()
});
var SupportTicketSchema = z16.discriminatedUnion("status", [OpenTicketSchema, ClosedTicketSchema]);
var SupportTicketMessageSchema = z16.object({
  tenantId: ulid,
  ticketId: ulid,
  messageId: ulid,
  authorUserId: z16.string().min(1),
  authorName: z16.string().min(1).optional(),
  authorSide: SupportTicketSideEnum,
  body: z16.string().min(1).max(4e3),
  createdAt: isoDate
});
var CreateSupportTicketSchema = z16.object({
  subject: z16.string().min(1).max(200),
  body: z16.string().min(1).max(4e3)
}).strict();
var CreateSupportTicketMessageSchema = z16.object({
  body: z16.string().min(1).max(4e3)
}).strict();
var CloseSupportTicketSchema = z16.object({
  status: z16.literal("closed"),
  closedReason: z16.string().min(1).max(500)
}).strict();
var ReopenSupportTicketSchema = z16.object({
  status: z16.literal("open")
}).strict();
var UpdateSupportTicketStatusSchema = z16.union([CloseSupportTicketSchema, ReopenSupportTicketSchema]);
var SupportTicketWithMessagesSchema = z16.discriminatedUnion("status", [
  OpenTicketSchema.extend({ messages: z16.array(SupportTicketMessageSchema) }),
  ClosedTicketSchema.extend({ messages: z16.array(SupportTicketMessageSchema) })
]);
var SupportTicketTmMessageSchema = SupportTicketMessageSchema;
var SupportTicketTmViewSchema = z16.discriminatedUnion("status", [
  OpenTicketSchema.extend({ tenantName: z16.string().min(1), messages: z16.array(SupportTicketTmMessageSchema) }),
  ClosedTicketSchema.extend({ tenantName: z16.string().min(1), messages: z16.array(SupportTicketTmMessageSchema) })
]);
var SupportTicketTmListItemSchema = z16.discriminatedUnion("status", [
  OpenTicketSchema.extend({ tenantName: z16.string().min(1) }),
  ClosedTicketSchema.extend({ tenantName: z16.string().min(1) })
]);

// src/template.ts
import { z as z17 } from "zod";
var AssignmentTemplateSchema = z17.object({
  /** Unique identifier for this template (ULID). */
  templateId: ulid,
  /** Tenant that owns this template. */
  tenantId: ulid,
  /** Human-readable name for the template (1-100 characters). */
  name: z17.string().min(1).max(100),
  /** Ordered list of policy IDs included in this template (1-25). */
  policyIds: z17.array(z17.string().min(1)).min(1).max(25),
  /** ISO-8601 timestamp when the template was last applied. */
  lastUsedAt: isoDate.optional(),
  /** ISO-8601 timestamp when the template was created. */
  createdAt: isoDate,
  /** ID of the user who created the template. */
  createdBy: z17.string().min(1),
  /** ISO-8601 timestamp when the template was last updated. */
  updatedAt: isoDate.optional()
});
var CreateAssignmentTemplateSchema = z17.object({
  /** Human-readable name for the template (1-100 characters). */
  name: z17.string().min(1).max(100),
  /** Ordered list of policy IDs included in this template (1-25). */
  policyIds: z17.array(z17.string().min(1)).min(1).max(25)
});
var UpdateAssignmentTemplateSchema = z17.object({
  /** Updated name for the template (1-100 characters). */
  name: z17.string().min(1).max(100).optional(),
  /** Updated list of policy IDs (1-25). */
  policyIds: z17.array(z17.string().min(1)).min(1).max(25).optional()
});
var ApplyTemplateRequestSchema = z17.object({
  /** IDs of the users to assign template policies to (at least 1). */
  userIds: z17.array(z17.string().min(1)).min(1),
  /** Optional IDs of the groups to assign template policies to. */
  groupIds: z17.array(z17.string().min(1)).optional(),
  /** Optional due date for the assignments (YYYY-MM-DD). */
  dueDate: z17.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional()
});

// src/user-directory.ts
import { z as z18 } from "zod";
var UserDirectoryStatusEnum = z18.enum(["ACTIVE", "DEACTIVATED"]);
var UserDirectoryRowSchema = z18.object({
  /** Lowercase email address (plus-aliases preserved). Partition key. */
  email: z18.string().min(1),
  /** Tenant the user belongs to. Sort key. */
  tenantId: z18.string().min(1),
  /** Cached subdomain of the tenant for fast central-login redirects. */
  subDomain: z18.string().min(1),
  /** Cached display name of the tenant for the tenant-picker UI. */
  tenantName: z18.string().min(1),
  /** Stable user identifier within the tenant (matches Cognito `sub`). */
  userId: z18.string().min(1),
  /** Tenant role of the user (e.g. `admin`, `signer`). Free-form string. */
  userType: z18.string().min(1),
  /** Lifecycle status — see `UserDirectoryStatusEnum`. */
  status: UserDirectoryStatusEnum,
  /** ISO-8601 timestamp of the most recent write to this row. */
  updatedAt: isoDate
}).strict();
export {
  AcknowledgePolicyByAuthRequestSchema,
  AcknowledgePolicyByTokenRequestSchema,
  AcknowledgmentViewItemSchema,
  AcknowledgmentViewSchema,
  AddMembersFailureSchema,
  AddMembersResultSchema,
  AdminActionEventDetailSchema,
  AnalyticsDateRangeSchema,
  ApplyTemplateRequestSchema,
  AssignmentDeclinedEventDetailSchema,
  AssignmentStatusEnum,
  AssignmentTemplateSchema,
  AssignmentsDateRangeQuerySchema,
  AuditActionEnum,
  AuditRecordSchema,
  AuditResourceTypeEnum,
  AuditorLinkRecordSchema,
  AuditorLinkStatusSchema,
  AuditorLinkSummarySchema,
  AutomatedReminderCadenceStepSchema,
  BaseAdminActionFields,
  BillingPortalSessionRequestSchema,
  BillingPortalSessionResponseSchema,
  BillingProvisionRequestSchema,
  BillingProvisionResponseSchema,
  BillingSummaryRequestSchema,
  BillingSummaryResponseSchema,
  BulkCreateUserRowSchema,
  BulkCreateUsersRequestSchema,
  BulkRevokeAssignmentsRequestSchema,
  CheckoutSessionRequestSchema,
  CheckoutSessionResponseSchema,
  CloseSupportTicketSchema,
  CompletionTrendPointSchema,
  CreateAssignmentTemplateSchema,
  CreateAuditorLinkRequestSchema,
  CreateAuditorLinkResponseSchema,
  CreateGroupSchema,
  CreateInitialUserSchema,
  CreatePolicyAssignmentRequestSchema,
  CreatePolicySchema,
  CreateScimTokenRequestSchema,
  CreateScimTokenResponseSchema,
  CreateSupportTicketMessageSchema,
  CreateSupportTicketSchema,
  CreateTenantSchema,
  CreateUserSchema,
  DEFAULT_CADENCE,
  DEFAULT_FEATURES,
  DashboardAnalyticsSchema,
  DeclineAssignmentRequestSchema,
  DemoRequestSchema,
  DigestFrequencySchema,
  ESIGN_CONSENT_TEXT,
  ESIGN_CONSENT_VERSION,
  ESIGN_INTENT_TEXT,
  EVENT_DETAIL_TYPE,
  EVENT_SCHEMAS,
  EVENT_SOURCE,
  EditAssignmentDueDateRequestSchema,
  GroupCompletionStatsSchema,
  GroupMembersAddedEventDetailSchema,
  GroupMembershipSchema,
  GroupPolicyAssignmentSchema,
  GroupPolicySummarySchema,
  GroupSchema,
  GroupStatsAggregateSchema,
  GroupWithMemberCountSchema,
  GroupWithMembersSchema,
  LoginTokenSchema,
  NotificationPreferencesSchema,
  OverdueBreakdownSchema,
  PRE_CONSENT_VERSION_SENTINEL,
  PaymentMethodSummarySchema,
  PolicyAcknowledgedEventDetailSchema,
  PolicyAcknowledgmentStatsSchema,
  PolicyAssignmentSchema,
  PolicyDeletedEventDetailSchema,
  PolicySchema,
  PolicyStatsAggregateSchema,
  PolicyTagsSchema,
  PolicyVersionSchema,
  RecurrenceTypeEnum,
  ReopenSupportTicketSchema,
  ResendWebhookEventSchema,
  ResendWebhookEventTypeSchema,
  RevokeAssignmentRequestSchema,
  SIGNER_JWT_ALG,
  SIGNER_KMS_SIGNING_ALGORITHM,
  SIGNER_TOKEN_ISSUER,
  SIGNER_TOKEN_TTL_SECONDS,
  SIGNER_USER_TYPE,
  STANDARD_BASE_USERS,
  SamlMetadataSchema,
  ScimTokenRecordSchema,
  ScimTokenSummarySchema,
  SelfServiceSignupSchema,
  SignerAssignmentSchema,
  SigningRequestRecipientSchema,
  SigningRequestsRequestedEventDetailSchema,
  SupportTicketMessageSchema,
  SupportTicketSchema,
  SupportTicketSideEnum,
  SupportTicketStatusEnum,
  SupportTicketTmListItemSchema,
  SupportTicketTmMessageSchema,
  SupportTicketTmViewSchema,
  SupportTicketWithMessagesSchema,
  SuppressedEmailSchema,
  SuppressionConfigurationSetSchema,
  SuppressionScopeSchema,
  SuppressionStatusSchema,
  TenantConfigurationChangedEventDetailSchema,
  TenantConfigurationDeletedEventDetailSchema,
  TenantConfigurationSchema,
  TenantCreatedEventDetailSchema,
  TenantDataSchema,
  TenantDeletedEventDetailSchema,
  TenantEntitlementsCreatedEventDetailSchema,
  TenantEntitlementsDeletedEventDetailSchema,
  TenantEntitlementsPeriodResetEventDetailSchema,
  TenantEntitlementsReplicatedSchema,
  TenantEntitlementsSchema,
  TenantEntitlementsUpdatedEventDetailSchema,
  TenantFeaturesSchema,
  TenantSchema,
  TenantUpdatedEventDetailSchema,
  TierSchema,
  TimeToSignByPolicySchema,
  USERS_PER_PACK,
  UpdateAssignmentTemplateSchema,
  UpdateGroupMembersSchema,
  UpdateGroupSchema,
  UpdateMeSchema,
  UpdatePolicySchema,
  UpdateSupportTicketStatusSchema,
  UpdateTenantSchema,
  UpdateUserPackQuantityRequestSchema,
  UpdateUserPackQuantityResponseSchema,
  UpdateUserSchema,
  UploadNewVersionRequestSchema,
  UserCreatedEventDetailSchema,
  UserDataSchema,
  UserDeletedEventDetailSchema,
  UserDirectoryRowSchema,
  UserDirectoryStatusEnum,
  UserSchema,
  UserStatusEnum,
  UserUpdatedEventDetailSchema,
  awsAccount,
  createPaginatedResponseSchema,
  emitEvent,
  emitEvents,
  getSamlMetadata,
  isoDate,
  nonEmptyLowercaseString,
  nonEmptyString,
  normalizeRecipients,
  paginationOptionsSchema,
  parseResendWebhookEvent,
  region,
  subDomain,
  ulid,
  userTypeEnum,
  validateEvent
};
